import { useState, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    alpha, AppBar, Box, Button, Container,
    Grid, IconButton, Link, Stack, Toolbar, Typography,
    useMediaQuery, useTheme,
} from "@mui/material";
import ThemeToggle from "../components/ThemeToggle";
import { APP_NAME, APP_TAGLINE } from "../config/brand";
import AppLogo from "../components/AppLogo";

import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ReportOutlinedIcon from "@mui/icons-material/ReportOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import GradingIcon from "@mui/icons-material/GradeOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AnnouncementOutlinedIcon from "@mui/icons-material/AnnouncementOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import EastIcon from "@mui/icons-material/East";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FacebookIcon from "@mui/icons-material/Facebook";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ContactMailOutlinedIcon from "@mui/icons-material/ContactMailOutlined";
import {
    SUBSCRIPTION_PLANS,
    PLAN_INCLUDES,
    TRIAL_DAYS,
    formatPriceMwk,
} from "../config/subscriptionPlans";

import imgCollaborative from "../assets/landing/collaborative-learning.png";
import imgDigital from "../assets/landing/digital-learning.png";
import imgAssembly from "../assets/landing/school-assembly.png";
import imgClassCommunity from "../assets/landing/class-community.png";
import imgSchoolBuilding from "../assets/landing/school-building.png";
import imgStudentPortrait from "../assets/landing/student-portrait.png";

// ─── Data ─────────────────────────────────────────────────────────────────────
const NAV = [
    { id: "pricing", label: "Pricing" },
    { id: "portals", label: "Portals" },
    { id: "features", label: "Features" },
    { id: "workflow", label: "How it works" },
    { id: "malawi", label: "Malawi built" },
    { id: "contact", label: "Contact" },
];

const CONTACT_LINKS = [
    {
        name: "Email",
        href: "mailto:zarilaasam99@gmail.com",
        icon: EmailOutlinedIcon,
    },
    {
        name: "Facebook",
        href: "https://web.facebook.com/samzarila/",
        icon: FacebookIcon,
    },
    {
        name: "X",
        href: "https://x.com/badboy_trox99",
        icon: null,
        xBrand: true,
    },
    {
        name: "WhatsApp",
        href: "https://wa.me/265899622111",
        icon: WhatsAppIcon,
    },
];

const STATS = [
    { value: "3", label: "Role portals", sub: "Director · Teacher · Student" },
    { value: "40+", label: "Modules", sub: "Fully integrated" },
    { value: "MIE", label: "Scheme format", sub: "Northern Ed. Division" },
    { value: "MANEB", label: "Curriculum", sub: "PSLCE · JCE · MSCE" },
];

const PORTALS = [
    {
        role: "School director",
        icon: AdminPanelSettingsOutlinedIcon,
        accent: "#e8c97e",
        summary: "Run the entire school from one dashboard — enrolment, finance, academics, HR, and compliance.",
        highlights: [
            "Students, classes, subjects & teachers",
            "Fees, expenses & budgets in MWK",
            "Marks & attendance approval",
            "Scheme of work review & sign-off",
            "School calendar & timetables",
        ],
    },
    {
        role: "Teacher",
        icon: MenuBookOutlinedIcon,
        accent: "#4ecdc4",
        summary: "Plan lessons, record marks and attendance, submit MIE-aligned schemes of work.",
        highlights: [
            "MIE 2-week scheme blocks with record of work",
            "Bulk attendance & marks entry",
            "Lesson plans with objectives",
            "Homework & assignment tracking",
            "Leave applications",
        ],
    },
    {
        role: "Student",
        icon: PersonOutlineOutlinedIcon,
        accent: "#a78bfa",
        summary: "View results, attendance records, resources, and communicate with school administration.",
        highlights: [
            "MANEB grade exam results",
            "University transcripts & GPA",
            "Subject attendance history",
            "Past papers & class notes",
            "Lodge complaints to admin",
        ],
    },
];

const FEATURE_GROUPS = [
    {
        label: "01  ·  Operations",
        title: "School administration",
        features: [
            { title: "Enrolment & profiles", desc: "Register learners with photos, guardians, district, and class placement.", icon: PersonOutlineOutlinedIcon },
            { title: "Classes & levels", desc: "Primary Std 1–8, Secondary Form 1–4, University Year 1–5 with exam class flags.", icon: AssignmentOutlinedIcon },
            { title: "Staff management", desc: "Teacher profiles, TSC numbers, contract types, and subject assignments.", icon: SupervisorAccountOutlinedIcon },
            { title: "Notices & complaints", desc: "Broadcast announcements by audience; resolve student complaints with audit trail.", icon: AnnouncementOutlinedIcon },
        ],
    },
    {
        label: "02  ·  Academics",
        title: "Teaching & learning",
        features: [
            { title: "Scheme of work (MIE)", desc: "Electronic Northern Education Division format with 2-week blocks, core elements, and record of work.", icon: EventNoteOutlinedIcon },
            {
                title: "Marks & results",
                desc: "Subject teachers → form teacher → director. Report cards publish to the student portal.",
                icon: GradingIcon,
            },
            { title: "Attendance registers", desc: "Subject-level attendance with weekly summaries and at-risk student alerts.", icon: FactCheckOutlinedIcon },
            { title: "Lesson plans & resources", desc: "Structured daily lesson documentation and uploaded class materials.", icon: FolderSharedOutlinedIcon },
        ],
    },
    {
        label: "03  ·  Finance",
        title: "Fees & expenditure",
        features: [
            { title: "Fee structures", desc: "Term-based fee setup per class — Tuition, PTA Levy, Development Levy, Exam Fee in MWK.", icon: PaymentsOutlinedIcon },
            { title: "Fee collection", desc: "Cash, Airtel Money, TNM Mpamba, bank transfer. Auto-generated receipts.", icon: SavingsOutlinedIcon },
            { title: "Defaulters & reports", desc: "Outstanding balances by class, CSV export, and term-end financial summaries.", icon: ReportOutlinedIcon },
            { title: "Expenses & budgets", desc: "Log ESCOM, water bills, and supplies. Set term budgets with spend-vs-budget tracking.", icon: SavingsOutlinedIcon },
        ],
    },
    {
        label: "04  ·  Exams & calendar",
        title: "Assessment & time",
        features: [
            { title: "MANEB grading", desc: "Automatic Grade 1–9 computation with Distinction, Merit, Credit, Pass, Fail labels.", icon: GradingIcon },
            { title: "University GPA", desc: "Credit hours, CA 40% + Exam 60% weighting, semester results, full transcripts.", icon: MenuBookOutlinedIcon },
            { title: "School calendar", desc: "Terms, exam schedules, events, and whole-school announcements.", icon: CalendarMonthOutlinedIcon },
            { title: "Curriculum guide", desc: "Built-in MIE/MANEB subject catalogue for all levels — no guesswork on subject codes.", icon: MapOutlinedIcon },
        ],
    },
];

const WORKFLOW_STEPS = [
    { n: "01", title: "Teacher prepares", body: "Build scheme blocks, enter marks or attendance, upload resources, apply for leave — all in the teacher portal." },
    { n: "02", title: "Submit for review", body: "Draft work moves to Submitted. Nothing becomes part of official records until the director acts." },
    { n: "03", title: "Director approves", body: "Review marks batches, attendance registers, schemes, and leave requests from the admin inbox." },
    { n: "04", title: "Official records", body: "Approved data flows into student results, attendance history, financial ledger, and Ministry reports." },
];

const MALAWI_POINTS = [
    "Primary, secondary, combined, and university school types",
    "Northern · Central · Southern regions with all 28 districts",
    "Three-term structure for primary/secondary, semester for university",
    "PSLCE, JCE, MSCE, and degree exam class detection",
    "Government, private, mission and community school ownership",
    "MANEB 1–9 grading scale with Distinction, Merit, Credit, Pass labels",
    "MIE scheme of work format with core elements and record of work",
    "Mobile money fee collection — Airtel Money and TNM Mpamba",
];

// ─── Animated counter ─────────────────────────────────────────────────────────
function AnimatedStat({ value, label, sub }) {
    const ref = useRef(null);
    const [vis, setVis] = useState(false);
    useEffect(() => {
        const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.3 });
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, []);
    return (
        <Box ref={ref} sx={{ textAlign: "center", py: 2, opacity: vis ? 1 : 0, transform: vis ? "none" : "translateY(12px)", transition: "opacity .6s ease, transform .6s ease" }}>
            <Typography sx={{ fontFamily: '"DM Serif Display",serif', fontSize: { xs: "2.8rem", md: "3.6rem" }, fontWeight: 400, color: "primary.main", lineHeight: 1 }}>
                {value}
            </Typography>
            <Typography variant="subtitle2" sx={{ mt: 0.5, color: "text.primary", fontFamily: '"DM Sans",sans-serif' }}>{label}</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: '"DM Sans",sans-serif' }}>{sub}</Typography>
        </Box>
    );
}

// ─── Reveal wrapper ───────────────────────────────────────────────────────────
function Reveal({ children, delay = 0, y = 20 }) {
    const ref = useRef(null);
    const [vis, setVis] = useState(false);
    useEffect(() => {
        const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.1 });
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, []);
    return (
        <Box ref={ref} sx={{ opacity: vis ? 1 : 0, transform: vis ? "none" : `translateY(${y}px)`, transition: `opacity .7s ease ${delay}ms, transform .7s ease ${delay}ms` }}>
            {children}
        </Box>
    );
}

// ─── Components ───────────────────────────────────────────────────────────────
function SectionTag({ children }) {
    return (
        <Typography component="div" sx={{
            display: "inline-flex", alignItems: "center", gap: 1,
            fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 700,
            letterSpacing: "0.12em", textTransform: "uppercase",
            color: "primary.main", mb: 2,
        }}>
            <Box sx={{ width: 18, height: 1.5, bgcolor: "primary.main", borderRadius: 1 }} />
            {children}
        </Typography>
    );
}

function PortalCard({ role, icon: Icon, accent, summary, highlights, index }) {
    return (
        <Reveal delay={index * 80}>
            <Box sx={{
                height: "100%", p: { xs: 3, md: 3.5 },
                background: "linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderTop: `2px solid ${accent}`,
                borderRadius: "2px",
                transition: "border-color .25s, transform .25s, box-shadow .25s",
                "&:hover": { borderColor: alpha(accent, 0.5), transform: "translateY(-4px)", boxShadow: `0 24px 60px rgba(0,0,0,0.4), 0 0 40px ${alpha(accent, 0.06)}` },
                display: "flex", flexDirection: "column",
            }}>
                <Box sx={{ width: 48, height: 48, borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: alpha(accent, 0.1), color: accent, mb: 2.5 }}>
                    <Icon />
                </Box>
                <Typography sx={{ fontFamily: '"DM Serif Display",serif', fontSize: "1.35rem", color: "text.primary", mb: 1 }}>{role}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, flex: 1, lineHeight: 1.7 }}>{summary}</Typography>
                <Stack spacing={0.8} sx={{ mb: 3 }}>
                    {highlights.map(h => (
                        <Stack key={h} direction="row" spacing={1} alignItems="flex-start">
                            <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: accent, mt: 0.8, flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: '"DM Sans",sans-serif', lineHeight: 1.5 }}>{h}</Typography>
                        </Stack>
                    ))}
                </Stack>
                <Button component={RouterLink} to="/login" variant="outlined" size="small" endIcon={<EastIcon fontSize="small" />}
                    sx={{ borderColor: alpha(accent, 0.35), color: accent, "&:hover": { borderColor: accent, bgcolor: alpha(accent, 0.06) }, fontFamily: '"DM Sans",sans-serif' }}>
                    Sign in
                </Button>
            </Box>
        </Reveal>
    );
}

function FeatureCard({ title, desc, icon: Icon, index }) {
    return (
        <Reveal delay={index * 60}>
            <Box sx={{
                p: 2.5, height: "100%",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: "2px",
                transition: "border-color .2s, background .2s",
                "&:hover": { borderColor: "rgba(232,201,126,0.2)", background: "rgba(232,201,126,0.025)" },
            }}>
                <Box sx={{ width: 36, height: 36, borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(232,201,126,0.08)", color: "primary.main", mb: 1.5 }}>
                    <Icon sx={{ fontSize: 20 }} />
                </Box>
                <Typography variant="subtitle2" sx={{ mb: 0.75, color: "text.primary", fontFamily: '"DM Sans",sans-serif', fontWeight: 600 }}>{title}</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6, fontFamily: '"DM Sans",sans-serif' }}>{desc}</Typography>
            </Box>
        </Reveal>
    );
}

function PricingSection() {
    return (
        <Box
            component="section"
            id="pricing"
            sx={{ py: { xs: 7, md: 10 }, borderTop: "1px solid rgba(255,255,255,0.05)" }}
        >
            <Container maxWidth="lg">
                <Reveal>
                    <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
                        <SectionTag>Simple pricing</SectionTag>
                        <Typography variant="h2" sx={{ fontSize: { xs: "1.8rem", md: "2.4rem" }, mb: 1.5 }}>
                            Plans for every school size
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 640, mx: "auto" }}>
                            Every plan includes setup help, support, usage guidance, and a public school website, plus
                            one <strong>{TRIAL_DAYS}-day free trial</strong> per school. Pay in MWK by bank or mobile
                            money (card gateway coming soon).
                        </Typography>
                    </Box>
                </Reveal>
                <Grid container spacing={2} alignItems="stretch">
                    {SUBSCRIPTION_PLANS.map((plan, i) => (
                        <Grid item xs={12} sm={6} md={4} lg={plan.id === "unlimited" ? 12 : 6} key={plan.id}>
                            <Reveal delay={i * 60}>
                                <Box
                                    sx={{
                                        height: "100%",
                                        p: 3,
                                        display: "flex",
                                        flexDirection: "column",
                                        border: plan.highlight
                                            ? "1px solid rgba(232,201,126,0.45)"
                                            : "1px solid rgba(255,255,255,0.08)",
                                        borderRadius: "4px",
                                        background: plan.highlight
                                            ? "linear-gradient(145deg, rgba(232,201,126,0.08) 0%, rgba(255,255,255,0.02) 100%)"
                                            : "rgba(255,255,255,0.02)",
                                        boxShadow: plan.highlight
                                            ? "0 24px 60px rgba(0,0,0,0.35)"
                                            : "none",
                                    }}
                                >
                                    {plan.highlight && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: "primary.main",
                                                fontWeight: 700,
                                                letterSpacing: "0.08em",
                                                textTransform: "uppercase",
                                                mb: 1,
                                                fontFamily: '"DM Sans",sans-serif',
                                            }}
                                        >
                                            Popular
                                        </Typography>
                                    )}
                                    <Typography
                                        sx={{
                                            fontFamily: '"DM Serif Display",serif',
                                            fontSize: "1.5rem",
                                            color: "text.primary",
                                            mb: 0.5,
                                        }}
                                    >
                                        {plan.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {plan.studentRange}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: '"DM Serif Display",serif',
                                            fontSize: "2rem",
                                            color: "primary.main",
                                            mb: 0.5,
                                        }}
                                    >
                                        {formatPriceMwk(plan.priceMwk)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                                        {plan.billingLabel}
                                    </Typography>
                                    <Stack spacing={0.75} sx={{ mb: 3, flex: 1 }}>
                                        {PLAN_INCLUDES.map((inc) => (
                                            <Stack key={inc} direction="row" spacing={1} alignItems="flex-start">
                                                <CheckCircleOutlineIcon
                                                    sx={{ fontSize: 16, color: "secondary.main", mt: 0.3 }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: "text.secondary",
                                                        fontFamily: '"DM Sans",sans-serif',
                                                        lineHeight: 1.5,
                                                    }}
                                                >
                                                    {inc}
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                    <Button
                                        component={RouterLink}
                                        to={`/Adminregister?plan=${plan.id}`}
                                        variant={plan.highlight ? "contained" : "outlined"}
                                        fullWidth
                                        endIcon={<EastIcon fontSize="small" />}
                                        sx={
                                            plan.highlight
                                                ? {}
                                                : {
                                                      borderColor: "rgba(255,255,255,0.15)",
                                                      color: "text.primary",
                                                  }
                                        }
                                    >
                                        Start free trial
                                    </Button>
                                </Box>
                            </Reveal>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
}

function GalleryCard({ src, title, caption, span, tall }) {
    return (
        <Grid item {...span}>
            <Box sx={{
                position: "relative",
                height: tall ? { xs: 260, md: 380 } : { xs: 200, md: 230 },
                borderRadius: "2px", overflow: "hidden",
                "&:hover img": { transform: "scale(1.05)" },
                "&::after": { content: '""', position: "absolute", inset: 0, border: "1px solid rgba(255,255,255,0.06)", borderRadius: "2px", pointerEvents: "none", zIndex: 2 },
            }}>
                <Box component="img" src={src} alt={title} sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform .6s cubic-bezier(.25,.46,.45,.94)" }} />
                <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(10,12,15,.88) 0%, rgba(10,12,15,.1) 55%)", zIndex: 1 }} />
                <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, p: 2, zIndex: 3 }}>
                    <Typography sx={{ fontFamily: '"DM Serif Display",serif', fontSize: "1rem", color: "#f0ede8", lineHeight: 1.3, mb: 0.4 }}>{title}</Typography>
                    <Typography variant="caption" sx={{ color: alpha("#f0ede8", 0.7), fontFamily: '"DM Sans",sans-serif', lineHeight: 1.4 }}>{caption}</Typography>
                </Box>
            </Box>
        </Grid>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────
function LandingPageContent() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const fn = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", fn, { passive: true });
        return () => window.removeEventListener("scroll", fn);
    }, []);

    const scrollTo = id => { document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }); setMobileOpen(false); };

    const GALLERY = [
        { src: imgClassCommunity, title: "Your school community", caption: "Teachers and learners united — the heart of every institution.", span: { xs: 12, md: 8 }, tall: true },
        { src: imgSchoolBuilding, title: "Modern campuses", caption: "From classroom blocks to leadership offices — one system.", span: { xs: 12, md: 4 }, tall: true },
        { src: imgCollaborative, title: "Collaborative learning", caption: "Group work reflected in attendance and class results.", span: { xs: 12, sm: 6, md: 4 } },
        { src: imgDigital, title: "Digital classrooms", caption: "Resources, assignments, and results online.", span: { xs: 12, sm: 6, md: 4 } },
        { src: imgAssembly, title: "Assemblies & events", caption: "Notices, calendars, and whole-school comms.", span: { xs: 12, sm: 6, md: 4 } },
        { src: imgStudentPortrait, title: "Every learner counts", caption: "Student portals for results, resources, and voice.", span: { xs: 12 } },
    ];

    return (
        <Box sx={{ bgcolor: "background.default", minHeight: "100vh", overflowX: "hidden" }}>
            {/* ── Navbar ─────────────────────────────────────────────────────── */}
            <AppBar position="fixed" elevation={0} sx={{
                bgcolor: (t) => scrolled ? alpha(t.palette.background.paper, 0.92) : "transparent",
                backdropFilter: scrolled ? "blur(20px)" : "none",
                borderBottom: scrolled ? (t) => `1px solid ${t.palette.divider}` : "none",
                transition: "background .3s, border .3s, backdrop-filter .3s",
            }}>
                <Toolbar sx={{ maxWidth: 1200, width: "100%", mx: "auto", px: { xs: 2, md: 3 }, minHeight: { xs: 56, md: 64 } }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1 }}>
                        <AppLogo size={32} />
                        <Typography sx={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 700, fontSize: "1.05rem", color: "text.primary" }}>
                            {APP_NAME}
                        </Typography>
                    </Stack>
                    {!isMobile ? (
                        <Stack direction="row" spacing={0} alignItems="center">
                            {NAV.map(item => (
                                <Button key={item.id} onClick={() => scrollTo(item.id)}
                                    sx={{ color: "text.secondary", fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 500, px: 1.5, "&:hover": { color: "text.primary", bgcolor: "transparent" } }}>
                                    {item.label}
                                </Button>
                            ))}
                            <Button
                                component={RouterLink}
                                to="/docs"
                                sx={{ color: "text.secondary", fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 500, px: 1.5, "&:hover": { color: "text.primary", bgcolor: "transparent" } }}
                            >
                                Documentation
                            </Button>
                            <Button component={RouterLink} to="/docs/getting-started" variant="outlined" size="small"
                                sx={{ ml: 2, mr: 1, fontFamily: '"DM Sans",sans-serif' }}>
                                View guide
                            </Button>
                            <ThemeToggle size="small" sx={{ mr: 1 }} />
                            <Button component={RouterLink} to="/Adminregister" variant="contained" size="small" endIcon={<EastIcon fontSize="small" />}>
                                Register school
                            </Button>
                        </Stack>
                    ) : (
                        <IconButton onClick={() => setMobileOpen(v => !v)} sx={{ color: "text.primary" }} aria-label="menu">
                            {mobileOpen ? <CloseIcon /> : <MenuIcon />}
                        </IconButton>
                    )}
                </Toolbar>
                {isMobile && mobileOpen && (
                    <Box sx={{ px: 2, pb: 2.5, borderTop: "1px solid rgba(255,255,255,0.06)", bgcolor: alpha("#0d1014", 0.98) }}>
                        <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                            {NAV.map(item => (
                                <Button key={item.id} fullWidth onClick={() => scrollTo(item.id)} sx={{ justifyContent: "flex-start", color: "text.secondary", fontFamily: '"DM Sans",sans-serif' }}>{item.label}</Button>
                            ))}
                            <Button
                                component={RouterLink}
                                to="/docs"
                                fullWidth
                                onClick={() => setMobileOpen(false)}
                                sx={{ justifyContent: "flex-start", color: "text.secondary", fontFamily: '"DM Sans",sans-serif' }}
                            >
                                Docs
                            </Button>
                            <Box sx={{ pt: 1, display: "flex", gap: 1 }}>
                                <Button component={RouterLink} to="/docs/getting-started" variant="outlined" fullWidth onClick={() => setMobileOpen(false)} sx={{ borderColor: "rgba(255,255,255,0.12)", color: "text.primary" }}>View guide</Button>
                                <Button component={RouterLink} to="/Adminregister" variant="contained" fullWidth>Register</Button>
                            </Box>
                        </Stack>
                    </Box>
                )}
            </AppBar>

            {/* ── Hero ───────────────────────────────────────────────────────── */}
            <Box component="section" sx={{ pt: { xs: 14, md: 18 }, pb: { xs: 8, md: 14 }, position: "relative", overflow: "hidden" }}>
                {/* Decorative bg blobs */}
                <Box sx={{ position: "absolute", top: "10%", right: "-5%", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(232,201,126,0.06) 0%, transparent 70%)", pointerEvents: "none" }} />
                <Box sx={{ position: "absolute", bottom: "5%", left: "-8%", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(78,205,196,0.04) 0%, transparent 70%)", pointerEvents: "none" }} />
                {/* Subtle grid lines */}
                <Box sx={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)", backgroundSize: "80px 80px", pointerEvents: "none" }} />

                <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                    <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <Reveal>
                                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, px: 1.5, py: 0.6, mb: 3, border: "1px solid rgba(232,201,126,0.25)", borderRadius: "3px" }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "primary.main" }} />
                                    <Typography variant="caption" sx={{ color: "primary.main", fontFamily: '"DM Sans",sans-serif', fontWeight: 600, letterSpacing: "0.1em" }}>
                                        SCHOOL MANAGEMENT SYSTEM · MALAWI
                                    </Typography>
                                </Box>
                            </Reveal>
                            <Reveal delay={80}>
                                <Typography variant="h1" sx={{ fontSize: { xs: "2.4rem", sm: "3rem", md: "3.75rem" }, mb: 2.5, color: "text.primary" }}>
                                    One platform for your entire school —<Box component="span" sx={{ color: "primary.main" }}> from scheme to fee collection.</Box>
                                </Typography>
                            </Reveal>
                            <Reveal delay={140}>
                                <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: 540, mb: 4, fontSize: "1.05rem" }}>
                                    Built for Malawian primary, secondary, and university institutions. Directors,
                                    teachers, and students each get a focused portal — with MIE/MANEB-aligned workflows
                                    and director approval gates on every official record.
                                </Typography>
                            </Reveal>
                            <Reveal delay={200}>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap">
                                    <Button component={RouterLink} to="/Adminregister" variant="contained" size="large" endIcon={<EastIcon />} sx={{ px: 3.5 }}>
                                        Register your school
                                    </Button>
                                    <Button
                                        component={RouterLink}
                                        to="/login"
                                        variant="outlined"
                                        size="large"
                                        sx={{
                                            borderColor: "rgba(0,0,0,0.6)",
                                            color: "text.primary",
                                            bgcolor: "rgba(0,0,0,0.02)",
                                            "&:hover": { borderColor: "text.primary", bgcolor: "rgba(0,0,0,0.08)" },
                                        }}
                                    >
                                        Sign in
                                    </Button>
                                    <Button
                                        onClick={() => scrollTo("pricing")}
                                        variant="outlined"
                                        size="large"
                                        sx={{
                                            borderColor: "rgba(232,201,126,0.35)",
                                            color: "primary.main",
                                            "&:hover": { borderColor: "primary.main", bgcolor: "rgba(232,201,126,0.06)" },
                                        }}
                                    >
                                        View pricing
                                    </Button>
                                    <Button component={RouterLink} to="/docs/getting-started" variant="outlined" size="large"
                                        sx={{ borderColor: "rgba(255,255,255,0.15)", color: "text.primary", "&:hover": { borderColor: "rgba(255,255,255,0.3)", bgcolor: "rgba(255,255,255,0.04)" } }}>
                                        View guide
                                    </Button>
                                </Stack>
                            </Reveal>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Reveal delay={160} y={30}>
                                <Box sx={{
                                    borderRadius: "4px", overflow: "hidden",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    background: "linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)",
                                }}>
                                    <Box sx={{ position: "relative", height: { xs: 220, md: 270 }, overflow: "hidden" }}>
                                        <Box component="img" src={imgClassCommunity} alt="School community" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                        <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(10,12,15,.8) 0%, transparent 60%)" }} />
                                        <Box sx={{ position: "absolute", bottom: 16, left: 16, right: 16 }}>
                                            <Typography sx={{ fontFamily: '"DM Serif Display",serif', fontSize: "1.1rem", color: "#f0ede8" }}>Built for real schools across Malawi</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ p: 2.5 }}>
                                        <Stack spacing={1.2}>
                                            {["Director dashboard with live fee & attendance KPIs","MIE electronic scheme of work & record of work","Teacher marks submission with director approval","Fee collection — Cash, Airtel Money, TNM Mpamba","Expenses, budgets & end-of-term finance reports"].map(line => (
                                                <Stack key={line} direction="row" spacing={1.25} alignItems="flex-start">
                                                    <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "primary.main", mt: 0.2, flexShrink: 0 }} />
                                                    <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: '"DM Sans",sans-serif', lineHeight: 1.55 }}>{line}</Typography>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    </Box>
                                </Box>
                            </Reveal>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* ── Stats ──────────────────────────────────────────────────────── */}
            <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)", bgcolor: alpha("#111418", 0.7), py: { xs: 2, md: 3 } }}>
                <Container maxWidth="lg">
                    <Grid container>
                        {STATS.map((s, i) => (
                            <Grid item xs={6} sm={3} key={s.label} sx={{ borderRight: i < 3 ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                                <AnimatedStat {...s} />
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ── Pricing (near top — right after hero stats) ───────────────── */}
            <PricingSection />

            {/* ── Gallery ────────────────────────────────────────────────────── */}
            <Box component="section" id="gallery" sx={{ py: { xs: 7, md: 11 } }}>
                <Container maxWidth="lg">
                    <Reveal>
                        <SectionTag>School life</SectionTag>
                        <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 5 }}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h2" sx={{ fontSize: { xs: "1.8rem", md: "2.4rem" } }}>
                                    Education in Malawi — people, places & progress
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="body1" color="text.secondary">
                                    {APP_NAME} supports the full journey: from classroom and assembly hall to digital learning
                                    and director oversight — aligned with how your school actually works.
                                </Typography>
                            </Grid>
                        </Grid>
                    </Reveal>
                    <Grid container spacing={2}>
                        {GALLERY.map((item, i) => <GalleryCard key={item.title} {...item} />)}
                    </Grid>
                </Container>
            </Box>

            {/* ── Portals ────────────────────────────────────────────────────── */}
            <Box component="section" id="portals" sx={{ py: { xs: 7, md: 11 }, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                <Container maxWidth="lg">
                    <Reveal>
                        <SectionTag>Role-based portals</SectionTag>
                        <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 5 }}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h2" sx={{ fontSize: { xs: "1.8rem", md: "2.4rem" } }}>
                                    Three portals, one connected school
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="body1" color="text.secondary">
                                    Every user sees only what they need. Data flows between roles — teachers submit,
                                    directors approve, records become official.
                                </Typography>
                            </Grid>
                        </Grid>
                    </Reveal>
                    <Grid container spacing={2}>
                        {PORTALS.map((p, i) => (
                            <Grid item xs={12} md={4} key={p.role}>
                                <PortalCard {...p} index={i} />
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ── Features ───────────────────────────────────────────────────── */}
            <Box component="section" id="features" sx={{ py: { xs: 7, md: 11 }, bgcolor: alpha("#0d1014", 0.6), borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                <Container maxWidth="lg">
                    <Reveal>
                        <SectionTag>Platform capabilities</SectionTag>
                        <Typography variant="h2" sx={{ fontSize: { xs: "1.8rem", md: "2.4rem" }, mb: 7 }}>
                            Every module your school needs
                        </Typography>
                    </Reveal>
                    <Stack spacing={9}>
                        {FEATURE_GROUPS.map((group, gi) => (
                            <Box key={group.title}>
                                <Reveal>
                                    <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 3 }}>
                                        <Grid item xs={12} md={5}>
                                            <Typography variant="caption" sx={{ color: "primary.main", fontFamily: '"DM Sans",sans-serif', fontWeight: 700, letterSpacing: "0.1em", display: "block", mb: 0.5 }}>
                                                {group.label}
                                            </Typography>
                                            <Typography variant="h3" sx={{ fontSize: { xs: "1.5rem", md: "1.85rem" } }}>{group.title}</Typography>
                                        </Grid>
                                    </Grid>
                                </Reveal>
                                <Grid container spacing={1.5}>
                                    {group.features.map((f, fi) => (
                                        <Grid item xs={12} sm={6} md={3} key={f.title}>
                                            <FeatureCard {...f} index={fi} />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        ))}
                    </Stack>
                </Container>
            </Box>

            {/* ── Workflow ───────────────────────────────────────────────────── */}
            <Box component="section" id="workflow" sx={{ py: { xs: 7, md: 11 }, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                <Container maxWidth="lg">
                    <Reveal>
                        <SectionTag>How it works</SectionTag>
                        <Typography variant="h2" sx={{ fontSize: { xs: "1.8rem", md: "2.4rem" }, mb: 6, maxWidth: 600 }}>
                            Teachers prepare. Directors approve. Records stay official.
                        </Typography>
                    </Reveal>
                    <Grid container spacing={2}>
                        {WORKFLOW_STEPS.map((w, i) => (
                            <Grid item xs={12} sm={6} md={3} key={w.n}>
                                <Reveal delay={i * 70}>
                                    <Box sx={{
                                        p: 3, height: "100%",
                                        border: "1px solid rgba(255,255,255,0.06)", borderRadius: "2px",
                                        position: "relative", overflow: "hidden",
                                        "&::before": { content: '""', position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${alpha("#e8c97e", i === 0 ? 0.8 : 0.2)} 0%, transparent 100%)` },
                                    }}>
                                        <Typography sx={{ fontFamily: '"DM Serif Display",serif', fontSize: "3rem", color: "primary.main", opacity: 0.18, lineHeight: 1, mb: 2 }}>
                                            {w.n}
                                        </Typography>
                                        <Typography variant="subtitle1" sx={{ color: "text.primary", mb: 1 }}>{w.title}</Typography>
                                        <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.65, fontFamily: '"DM Sans",sans-serif' }}>{w.body}</Typography>
                                    </Box>
                                </Reveal>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ── Malawi context ─────────────────────────────────────────────── */}
            <Box component="section" id="malawi" sx={{ py: { xs: 7, md: 10 }, borderTop: "1px solid rgba(255,255,255,0.05)", position: "relative", overflow: "hidden" }}>
                <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(232,201,126,0.04) 0%, rgba(78,205,196,0.03) 100%)", pointerEvents: "none" }} />
                <Box sx={{ position: "absolute", top: "-30%", right: "-10%", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(232,201,126,0.05) 0%, transparent 65%)", pointerEvents: "none" }} />
                <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                    <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <Reveal>
                                <SectionTag>Built for Malawi</SectionTag>
                                <Typography variant="h2" sx={{ fontSize: { xs: "1.8rem", md: "2.3rem" }, mb: 2 }}>
                                    Ministry-aligned structure, local school realities
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                    Configure school type, region, district, and ownership. Scheme of work follows the
                                    Northern Education Division electronic format with 2-week blocks, success criteria,
                                    and record of work sections.
                                </Typography>
                                <Button component={RouterLink} to="/Adminregister" variant="contained" size="large" endIcon={<EastIcon />}>
                                    Register your school
                                </Button>
                            </Reveal>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Grid container spacing={1.5}>
                                {MALAWI_POINTS.map((point, i) => (
                                    <Grid item xs={12} sm={6} key={point}>
                                        <Reveal delay={i * 50}>
                                            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{
                                                p: 1.5,
                                                border: "1px solid rgba(255,255,255,0.06)", borderRadius: "2px",
                                                transition: "border-color .2s",
                                                "&:hover": { borderColor: "rgba(232,201,126,0.2)" },
                                            }}>
                                                <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "primary.main", mt: 0.2, flexShrink: 0 }} />
                                                <Typography variant="body2" sx={{ color: "text.secondary", fontFamily: '"DM Sans",sans-serif', lineHeight: 1.5 }}>{point}</Typography>
                                            </Stack>
                                        </Reveal>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* ── Contact ────────────────────────────────────────────────────── */}
            <Box
                id="contact"
                component="section"
                sx={{ py: { xs: 7, md: 10 }, borderTop: "1px solid rgba(255,255,255,0.05)" }}
            >
                <Container maxWidth="lg">
                    <Reveal>
                        <Stack spacing={1} alignItems="flex-start" sx={{ mb: 4 }}>
                            <SectionTag>Get in touch</SectionTag>
                            <Typography variant="h2" sx={{ fontSize: { xs: "1.75rem", md: "2.25rem" } }}>
                                Contact us
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 520 }}>
                                Questions about registering your school, pricing, or support? Reach out by email,
                                social media, or WhatsApp.
                            </Typography>
                        </Stack>
                    </Reveal>
                    <Grid container spacing={2}>
                        {CONTACT_LINKS.map((item, i) => {
                            const Icon = item.icon;
                            return (
                                <Grid item xs={12} sm={6} md={3} key={item.name}>
                                    <Reveal delay={i * 60}>
                                        <Box
                                            component="a"
                                            href={item.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={`Contact via ${item.name}`}
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                textAlign: "center",
                                                height: "100%",
                                                minHeight: 120,
                                                p: 2.5,
                                                textDecoration: "none",
                                                color: "inherit",
                                                border: "1px solid rgba(255,255,255,0.08)",
                                                borderRadius: "4px",
                                                transition: "border-color .2s, background .2s",
                                                "&:hover": {
                                                    borderColor: "rgba(232,201,126,0.35)",
                                                    bgcolor: "rgba(232,201,126,0.04)",
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 44,
                                                    height: 44,
                                                    borderRadius: "4px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    bgcolor: alpha("#e8c97e", 0.12),
                                                    color: "primary.main",
                                                    mb: 1.5,
                                                }}
                                            >
                                                {item.xBrand ? (
                                                    <Typography
                                                        component="span"
                                                        sx={{
                                                            fontWeight: 800,
                                                            fontSize: "1.1rem",
                                                            fontFamily: '"DM Sans",sans-serif',
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        𝕏
                                                    </Typography>
                                                ) : Icon ? (
                                                    <Icon sx={{ fontSize: 24 }} />
                                                ) : (
                                                    <ContactMailOutlinedIcon sx={{ fontSize: 24 }} />
                                                )}
                                            </Box>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    color: "text.primary",
                                                    fontFamily: '"DM Sans",sans-serif',
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {item.name}
                                            </Typography>
                                        </Box>
                                    </Reveal>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Container>
            </Box>

            {/* ── CTA ────────────────────────────────────────────────────────── */}
            <Box component="section" sx={{ py: { xs: 7, md: 10 }, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                <Container maxWidth="md">
                    <Reveal>
                        <Box sx={{
                            p: { xs: 4, md: 6 }, textAlign: "center",
                            border: "1px solid rgba(232,201,126,0.15)", borderRadius: "4px",
                            background: "linear-gradient(145deg, rgba(232,201,126,0.04) 0%, rgba(78,205,196,0.02) 100%)",
                            position: "relative", overflow: "hidden",
                        }}>
                            <Box sx={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: "60%", height: 1, background: "linear-gradient(90deg, transparent, rgba(232,201,126,0.4), transparent)" }} />
                            <Typography variant="h2" sx={{ fontSize: { xs: "1.8rem", md: "2.5rem" }, mb: 1.5 }}>
                                Ready to modernise your school?
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 480, mx: "auto" }}>
                                Register as a school director with a {TRIAL_DAYS}-day free trial, or sign in as teacher
                                or student. Every plan includes a school website for parents.
                            </Typography>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
                                <Button component={RouterLink} to="/Adminregister" variant="contained" size="large" endIcon={<EastIcon />} sx={{ px: 4 }}>
                                    Register your school
                                </Button>
                                <Button
                                    component={RouterLink}
                                    to="/docs/getting-started"
                                    variant="outlined"
                                    size="large"
                                    sx={{
                                        borderColor: "rgba(255,255,255,0.15)",
                                        color: "text.primary",
                                        "&:hover": { borderColor: "rgba(255,255,255,0.3)", bgcolor: "rgba(255,255,255,0.04)" },
                                    }}
                                >
                                    View guide
                                </Button>
                                <Button
                                    onClick={() => scrollTo("pricing")}
                                    size="large"
                                    sx={{ color: "text.secondary", "&:hover": { color: "text.primary", bgcolor: "transparent" } }}
                                >
                                    View pricing
                                </Button>
                            </Stack>
                        </Box>
                    </Reveal>
                </Container>
            </Box>

            {/* ── Footer ─────────────────────────────────────────────────────── */}
            <Box component="footer" sx={{ py: 3, borderTop: "1px solid rgba(255,255,255,0.06)", bgcolor: alpha("#0d1014", 0.8) }}>
                <Container maxWidth="lg">
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="center" spacing={1}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <AppLogo size={22} />
                            <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: '"DM Sans",sans-serif' }}>
                                © {new Date().getFullYear()} {APP_NAME} · {APP_TAGLINE}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={2.5} flexWrap="wrap" justifyContent={{ xs: "center", sm: "flex-end" }}>
                            {[
                                { to: "/docs/getting-started", label: "View guide", router: true },
                                { to: "/Adminregister", label: "Register", router: true },
                                { id: "contact", label: "Contact", router: false },
                            ].map((l) =>
                                l.router ? (
                                    <Link
                                        key={l.to}
                                        component={RouterLink}
                                        to={l.to}
                                        underline="none"
                                        sx={{
                                            color: "text.secondary",
                                            fontFamily: '"DM Sans",sans-serif',
                                            fontSize: 13,
                                            "&:hover": { color: "text.primary" },
                                            transition: "color .15s",
                                        }}
                                    >
                                        {l.label}
                                    </Link>
                                ) : (
                                    <Link
                                        key={l.id}
                                        component="button"
                                        type="button"
                                        onClick={() => scrollTo(l.id)}
                                        underline="none"
                                        sx={{
                                            color: "text.secondary",
                                            fontFamily: '"DM Sans",sans-serif',
                                            fontSize: 13,
                                            border: "none",
                                            bgcolor: "transparent",
                                            cursor: "pointer",
                                            p: 0,
                                            "&:hover": { color: "text.primary" },
                                            transition: "color .15s",
                                        }}
                                    >
                                        {l.label}
                                    </Link>
                                ),
                            )}
                        </Stack>
                    </Stack>
                </Container>
            </Box>
        </Box>
    );
}

export default function LandingPage() {
    return <LandingPageContent />;
}